import type { ValueOf } from 'cpool-utils';
import { create } from 'zustand';

import type { ApplicantFilter } from '~/features/whatsapp/utils/resolveApplicantChats';
import { createSelectors } from '~/store/utils/createSelectors';

export const CHAT_PANEL_VIEWS = {
    CHAT: 'chat',
    LIST: 'list',
    USER_INFO: 'userInfo',
} as const;

export type ChatPanelView = ValueOf<typeof CHAT_PANEL_VIEWS>;

export const CHAT_CONTACT_TABS = {
    CUSTOMERS: 'CUSTOMERS',
    PARTNERS: 'PARTNERS',
} as const;

export type ChatContactTab = ValueOf<typeof CHAT_CONTACT_TABS>;

export const CHAT_LIST_MODES = {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
} as const;

export type ChatListMode = ValueOf<typeof CHAT_LIST_MODES>;

interface ChatPanelState {
    isOpen: boolean;
    viewStack: Array<ChatPanelView>;
    activeChatId: string | null;
    applicantFilter: ApplicantFilter | null;
    autoOpenTemplatePicker: boolean;
    contactTab: ChatContactTab;
    leadDetailsScopeLoanRequestId: string | null;
    listMode: ChatListMode;
    pendingChatIds: Array<string>;
}

const initialState: ChatPanelState = {
    activeChatId: null,
    applicantFilter: null,
    autoOpenTemplatePicker: false,
    contactTab: CHAT_CONTACT_TABS.CUSTOMERS,
    isOpen: false,
    leadDetailsScopeLoanRequestId: null,
    listMode: CHAT_LIST_MODES.ACTIVE,
    pendingChatIds: [],
    viewStack: [CHAT_PANEL_VIEWS.LIST],
};

const useChatPanelBaseStore = create<ChatPanelState>()(() => ({ ...initialState }));

export const useChatPanelStore = createSelectors(useChatPanelBaseStore);

export const useChatPanelCurrentView = () =>
    useChatPanelBaseStore((state) => state.viewStack[state.viewStack.length - 1]);

export type OpenPanelOptions = {
    autoOpenTemplatePicker?: boolean;
    chatId?: string;
    leadDetailsScopeLoanRequestId?: string;
    pendingChatIds?: Array<string>;
};

export const openPanel = (options: OpenPanelOptions = {}) => {
    useChatPanelBaseStore.setState({
        activeChatId: options.chatId ?? null,
        applicantFilter: null,
        autoOpenTemplatePicker: options.autoOpenTemplatePicker === true,
        isOpen: true,
        leadDetailsScopeLoanRequestId: options.leadDetailsScopeLoanRequestId ?? null,
        listMode: CHAT_LIST_MODES.ACTIVE,
        pendingChatIds: options.pendingChatIds ?? [],
        viewStack: options.chatId ? [CHAT_PANEL_VIEWS.LIST, CHAT_PANEL_VIEWS.CHAT] : [CHAT_PANEL_VIEWS.LIST],
    });
};

export const clearAutoOpenTemplatePicker = () => {
    useChatPanelBaseStore.setState({ autoOpenTemplatePicker: false });
};

export const closePanel = () => {
    useChatPanelBaseStore.setState({ ...initialState });
};

/** Clear active chat and return to list; keeps panel open and lead scope. */
export const leaveActiveChat = () => {
    useChatPanelBaseStore.setState({
        activeChatId: null,
        applicantFilter: null,
        autoOpenTemplatePicker: false,
        pendingChatIds: [],
        viewStack: [CHAT_PANEL_VIEWS.LIST],
    });
};

export const pushChatPanelView = (view: ChatPanelView) => {
    useChatPanelBaseStore.setState((state) => ({ viewStack: [...state.viewStack, view] }));
};

export const popChatPanelView = () => {
    useChatPanelBaseStore.setState((state) =>
        state.viewStack.length > 1 ? { viewStack: state.viewStack.slice(0, -1) } : state,
    );
};

export const setActiveChat = (chatId: string) => {
    useChatPanelBaseStore.setState((state) => ({
        activeChatId: chatId,
        applicantFilter: null,
        viewStack: [...state.viewStack, CHAT_PANEL_VIEWS.CHAT],
    }));
};

export const leaveLeadScope = () => {
    useChatPanelBaseStore.setState({ leadDetailsScopeLoanRequestId: null, pendingChatIds: [] });
};

export const setContactTab = (contactTab: ChatContactTab) => {
    useChatPanelBaseStore.setState({ contactTab });
};

export const setListMode = (listMode: ChatListMode) => {
    useChatPanelBaseStore.setState({ listMode });
};

export const setApplicantFilter = (applicantFilter: ApplicantFilter | null) => {
    useChatPanelBaseStore.setState({ applicantFilter });
};

export const switchApplicantChat = (
    chatId: string | null,
    applicantFilter: ApplicantFilter,
    options?: { autoOpenTemplatePicker?: boolean },
) => {
    useChatPanelBaseStore.setState((state) => ({
        activeChatId: chatId ?? state.activeChatId,
        applicantFilter,
        ...(options?.autoOpenTemplatePicker === true ? { autoOpenTemplatePicker: true } : {}),
    }));
};
